import type { AnyAction, Middleware } from 'redux';
// noinspection JSUnusedLocalSymbols

import { proto, types } from '../../../proto/ListenerProto';
import { loginWithCompany, types as authTypes } from '../../actions/backendAuth';
import {
    daemonVersionReceived,
    deviceListReceived,
    deviceListReceiveError,
    mainConnectionClosed,
    mainConnectionOpened,
    startAudioDeviceConnection,
    startAudioSystemInfoConnection,
    startEventForwardingConnection,
    stopMainConnection,
    types as mainTypes
} from '../../actions/backendMain';
import MainConnection from '../../../services/MainConnection';
import { filterDefaultDevices, semverDiff } from '../../../utils/background/Utils';
import { reload, types as commonTypes } from '../../actions/backendCommon';
import { isMainConnectionOpen } from '../../selectors/call';
import { getAuthToken } from '../../selectors/auth';
import { captureException } from '../../../tracker/raven';
import { audioSysLogger, mainConnLogger as logger } from '../../../utils/logger/loggerFactory';
import { errorCodes } from '../../../utils/utils';

import { Common, MainConn } from '../../../errors/Errors';
import { getListenerRequestError, getRequestType } from '../../../utils/background/error';
import { DAEMON_VERSION, wsConnectionClosingCodes } from '../../../constants/backend';
import { IS_LEGACY_LISTENER_MODE } from '../../../config/electron';
import { dispatchBackend } from '../../types/backend';
import type { AudioDeviceInfo, AuthSystemInfo, RootState } from '../../types';

const { UNEXPECTED_DAEMON_VERSION, MAIN_CONNECTION_CLOSED, HARDWARE_ERROR, MAIN_CONNECTION_CLOSED_ON_START } = errorCodes.background;

const { INTERNAL_ERROR, INVALID_REQUEST } = proto.Status;

const ErrorTypes = {
    [INTERNAL_ERROR]: MainConn.InternalError,
    [INVALID_REQUEST]: MainConn.InvalidRequestError
};

const mainLog = logger;
const audioLog = audioSysLogger;

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isBinaryLike(value: unknown): boolean {
    return value instanceof Uint8Array || ArrayBuffer.isView(value);
}

function getPayloadToken(payload: Record<string, unknown> | undefined): string {
    return typeof payload?.token === 'string' ? payload.token : '';
}

function toError(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }
    return new Error(typeof error === 'string' ? error : 'Unknown ListenerMainConnection error');
}

type ListenerAction = AnyAction & {
    payload?: Record<string, unknown>;
};

function getPayloadWithSystemInfo(payload?: Record<string, unknown>): AuthSystemInfo | null {
    if (payload && isRecord(payload.systemInfo)) {
        const systemInfo = payload.systemInfo;
        return {
            companyName: typeof systemInfo.companyName === 'string' ? systemInfo.companyName : undefined,
            username: typeof systemInfo.username === 'string' ? systemInfo.username : undefined,
            companySecretToken: typeof systemInfo.companySecretToken === 'string' ? systemInfo.companySecretToken : undefined,
            uniqueId: typeof systemInfo.uniqueId === 'string' ? systemInfo.uniqueId : undefined,
            toJSON: systemInfo.toJSON
        };
    }
    return null;
}

function isAudioDeviceInfo(value: unknown): value is AudioDeviceInfo {
    if (!isRecord(value)) {
        return false;
    }

    const entries = Object.values(value);
    return (
        entries.length > 0 &&
        entries.every(item => item === null || isBinaryLike(item) || ['string', 'number', 'boolean'].includes(typeof item))
    );
}

function getPayloadDevices(payload?: Record<string, unknown>): { mics: AudioDeviceInfo[]; speakers: AudioDeviceInfo[] } {
    const mics = payload && Array.isArray(payload.mics) ? payload.mics.filter(isAudioDeviceInfo) : [];
    const speakers = payload && Array.isArray(payload.speakers) ? payload.speakers.filter(isAudioDeviceInfo) : [];
    return { mics, speakers };
}

function createListenerMainConnection(dispatch: (action: unknown) => void) {
    // Main listener connection endpoint.
    const connection = new MainConnection({ host: 'localhost', port: 51873 });
    connection.on('opened', () => {
        dispatch(mainConnectionOpened());
    });
    connection.on('closed', ({ wasClean, code, reason }: { wasClean: boolean; code?: number; reason?: string }) => {
        if (wasClean) {
            mainLog.log('closed gracefully');
        } else {
            mainLog.error(`closed with error, code: ${code}, reason: ${reason}`);
        }
        dispatch(mainConnectionClosed(wasClean, code));
    });
    connection.on('message', (data: Uint8Array) => {
        const message = proto.Response.decode(data);
        switch (message.responseType) {
            case types.AUDIOSYSTEM_NOTIFICATOR_ENDPOINT: {
                const { port } = message.audiosystemNotificatorEndpoint;
                dispatch(startAudioSystemInfoConnection(port));
                break;
            }
            case types.AUTHORIZATION_PROMPT: {
                const sysInfo = message.authorizationPrompt.systemInformation;
                dispatch({ type: authTypes.SYSTEM_INFO_RECEIVED, payload: { systemInfo: sysInfo } });
                break;
            }
            case types.SYSTEM_INFORMATION: {
                dispatch({ type: authTypes.SYSTEM_INFO_UPDATED, payload: { systemInfo: message.systemInformation } });
                break;
            }
            case types.SERVICE_VERSION: {
                const { major, minor, patch, hasOpus } = message.serviceVersion;
                (window as Window & { hasOpus?: boolean }).hasOpus = !!hasOpus;
                const version = `${major}.${minor}.${patch}`;
                dispatch(daemonVersionReceived(version));
                break;
            }
            // @deprecated Old Listener APP
            case types.ACTIVE_AUDIO_DEVICES: {
                const { audioDevices } = message.activeAudioDevices;
                const defaultDevices = filterDefaultDevices(audioDevices);
                const { mics, speakers } = defaultDevices;
                // Validate that at least one mic and one speaker are present.
                dispatch(deviceListReceived(mics, speakers));
                break;
            }
            case types.DEVICE_ENDPOINT: {
                const { port } = message.deviceEndpoint;
                dispatch(startAudioDeviceConnection(port));
                break;
            }
            case types.EVENT_BROADCASTER_ENDPOINT: {
                const { port } = message.eventBroadcasterEndpoint;
                dispatch(startEventForwardingConnection(port));
                break;
            }
            case types.SERVICE_MESSAGE: {
                const { status } = message.serviceMessage;
                const serviceMessage = message.serviceMessage.toJSON();
                mainLog.log('Service message', serviceMessage);

                if (status === INTERNAL_ERROR || status === INVALID_REQUEST) {
                    const extra = { debugInfo: serviceMessage };
                    const { metainfo = [], initialRequest = {} } = message.serviceMessage;
                    const requestType = getRequestType(initialRequest);
                    let error = null;

                    if (!requestType) {
                        // Default type in case serviceMessage does not have a request type
                        const ErrorCtor = (ErrorTypes as Record<number, new (p: unknown) => Error>)[status];
                        error = new ErrorCtor({ status, extra, metainfo });
                    } else {
                        error = getListenerRequestError(status, requestType, metainfo, extra);
                    }

                    const actualError = toError(error);
                    captureException(actualError);
                    mainLog.error(actualError.message, actualError);
                }

                if (status === INTERNAL_ERROR) {
                    dispatch(reload({ code: HARDWARE_ERROR, reason: 'HARDWARE_ERROR' }));
                }
                break;
            }
            default:
                mainLog.warn('Unsupported message type', message);
        }
    });
    return connection;
}

const listenerMainConnection: Middleware = store => {
    const dispatch = (action: unknown) => dispatchBackend(store.dispatch, action);
    const getState = store.getState as () => RootState;
    const connection = createListenerMainConnection(dispatch);

    return next => (action: ListenerAction) => {
        const typedAction = action;
        const { type, payload } = typedAction;
        const isOpen = isMainConnectionOpen(getState());
        const result = next(action);
        const state = getState();

        switch (type) {
            case commonTypes.RELOAD: {
                if (isMainConnectionOpen(state)) {
                    dispatch(stopMainConnection());
                }
                break;
            }
            case mainTypes.START_MAIN_CONNECTION: {
                if (!IS_LEGACY_LISTENER_MODE) {
                    break;
                }

                if (!isMainConnectionOpen(state)) {
                    connection.start();
                } else {
                    const error = new MainConn.AlreadyStartedError();
                    captureException(error);
                    mainLog.error(error.message);
                }
                break;
            }
            case commonTypes.CLOSE_ALL_CONNECTIONS:
            case mainTypes.STOP_MAIN_CONNECTION: {
                if (isMainConnectionOpen(state)) {
                    // Cases when closing the connection without reload OR with reload
                    const code = typeof payload?.code === 'string' ? payload.code : String(wsConnectionClosingCodes.DEFAULT);
                    connection.finishConnection(code);
                }
                break;
            }
            case authTypes.SYSTEM_INFO_RECEIVED: {
                const systemInfo = getPayloadWithSystemInfo(payload);
                if (!systemInfo) {
                    break;
                }

                const { companyName = '', username = '', companySecretToken = '', uniqueId = '' } = systemInfo;
                if (!getAuthToken(state)) {
                    dispatch(loginWithCompany(companyName, username, companySecretToken, uniqueId));
                }
                break;
            }
            case mainTypes.INIT_LISTENER: {
                connection.sendAuthorizationRequest(String(state.user.profile?.secret || ''));
                connection.sendRefreshTokenRequest(String(getAuthToken(state) || ''));
                connection.sendServiceVersionRequest();
                connection.sendEventBroadcasterRequest();
                break;
            }
            case authTypes.TOKEN_REFRESH_SUCCESS: {
                if (isMainConnectionOpen(state)) {
                    connection.sendRefreshTokenRequest(getPayloadToken(payload));
                }
                break;
            }
            case mainTypes.REQUEST_LEGACY_LISTENER_DEVICE_LIST:
                connection.sendDeviceListRequest();
                break;
            case mainTypes.DEVICE_LIST_RECEIVED: {
                const { mics, speakers } = getPayloadDevices(payload);

                if (mics.length > 0 && speakers.length > 0) {
                    connection.sendStartMixedRecordingRequest([...mics, ...speakers]);
                    break;
                }
                if (mics.length === 0) {
                    const micError = new Common.MicDeviceNotFoundError();
                    captureException(micError);
                    audioLog.error(micError.message, micError);
                    dispatch(deviceListReceiveError());
                }
                if (speakers.length === 0) {
                    const speakerError = new Common.SpeakerDeviceNotFoundError();
                    captureException(speakerError);
                    audioLog.error(speakerError.message, speakerError);
                    dispatch(deviceListReceiveError());
                }
                break;
            }
            case mainTypes.GET_SYSTEM_INFORMATION: {
                connection.sendSystemInformationRequest();
                break;
            }
            case mainTypes.DAEMON_VERSION_RECEIVED: {
                const actual = typeof payload?.version === 'string' ? payload.version : '';
                const expected = DAEMON_VERSION ?? '';
                const diff = semverDiff(actual, expected);

                if (diff === null) {
                    break;
                }

                if (diff !== 'major') {
                    mainLog.warn(`Daemon version patch mismatch. Expected: ${expected}, received: ${actual}`);
                } else {
                    mainLog.error(`Daemon version mismatch. Expected: ${expected}, received: ${actual}`);

                    dispatch(
                        reload({
                            code: UNEXPECTED_DAEMON_VERSION,
                            reason: 'UNEXPECTED_DAEMON_VERSION'
                        })
                    );
                }
                break;
            }
            case mainTypes.MAIN_CONNECTION_CLOSED: {
                const { code = null } = payload || {};

                if (code === wsConnectionClosingCodes.NO_RELOAD) return;

                if (isOpen) {
                    dispatch(
                        reload({
                            code: MAIN_CONNECTION_CLOSED,
                            reason: 'MAIN_CONNECTION_CLOSED'
                        })
                    );
                } else {
                    dispatch(
                        reload({
                            code: MAIN_CONNECTION_CLOSED_ON_START,
                            reason: 'MAIN_CONNECTION_CLOSED_ON_START'
                        })
                    );
                }

                break;
            }

            default: // Do nothing
        }

        return result;
    };
};

export default listenerMainConnection;
